/* eslint-disable react-refresh/only-export-components -- hook exported alongside components */
/**
 * Global error context for user feedback
 * Provides error notifications/toasts across the application
 */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { getUserMessage, type AppError } from '@/utils/errors';
import { logger } from '@/utils/logger';

export type ErrorSeverity = 'error' | 'warning' | 'info';

export interface ErrorNotification {
  id: string;
  message: string;
  severity: ErrorSeverity;
  timestamp: Date;
  error?: AppError;
  dismissable: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ErrorContextType {
  /** Current error notifications */
  notifications: ErrorNotification[];
  /** Show an error notification to the user */
  showError: (error: unknown, options?: ShowErrorOptions) => string;
  /** Show a warning notification */
  showWarning: (message: string, options?: Omit<ShowErrorOptions, 'severity'>) => string;
  /** Show an info notification */
  showInfo: (message: string, options?: Omit<ShowErrorOptions, 'severity'>) => string;
  /** Dismiss a specific notification */
  dismiss: (id: string) => void;
  /** Dismiss all notifications */
  dismissAll: () => void;
  /** Clear notifications older than specified age (in ms) */
  clearOld: (maxAge: number) => void;
}

interface ShowErrorOptions {
  /** Custom message to show (overrides auto-generated message) */
  message?: string;
  /** Severity level */
  severity?: ErrorSeverity;
  /** Whether user can dismiss the notification (default: true) */
  dismissable?: boolean;
  /** Auto-dismiss after this many ms (0 = never) */
  autoDismiss?: number;
  /** Action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

const ErrorContext = createContext<ErrorContextType | null>(null);

/** Generate unique ID for notifications */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const DEFAULT_AUTO_DISMISS = {
  error: 0, // Errors stay until dismissed
  warning: 8000, // 8 seconds
  info: 5000, // 5 seconds
};

export function ErrorProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<ErrorNotification[]>([]);

  const dismiss = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const clearOld = useCallback((maxAge: number) => {
    const cutoff = Date.now() - maxAge;
    setNotifications(prev => prev.filter(n => n.timestamp.getTime() > cutoff));
  }, []);

  const addNotification = useCallback((notification: ErrorNotification, autoDismiss: number) => {
    setNotifications(prev => {
      // Prevent duplicate messages within 2 seconds
      const isDuplicate = prev.some(
        n => n.message === notification.message &&
             notification.timestamp.getTime() - n.timestamp.getTime() < 2000
      );
      if (isDuplicate) return prev;

      // Keep max 5 notifications
      const updated = [...prev, notification].slice(-5);
      return updated;
    });

    // Auto-dismiss if configured
    if (autoDismiss > 0) {
      setTimeout(() => dismiss(notification.id), autoDismiss);
    }
  }, [dismiss]);

  const showError = useCallback((error: unknown, options?: ShowErrorOptions): string => {
    const id = generateId();
    const severity = options?.severity ?? 'error';
    const message = options?.message ?? getUserMessage(error);

    // Log the error
    if (severity === 'error') {
      logger.error('[ErrorContext]', error);
    } else {
      logger.warn('[ErrorContext]', message);
    }

    const notification: ErrorNotification = {
      id,
      message,
      severity,
      timestamp: new Date(),
      error: error instanceof Error && 'code' in error ? error as AppError : undefined,
      dismissable: options?.dismissable ?? true,
      action: options?.action,
    };

    const autoDismiss = options?.autoDismiss ?? DEFAULT_AUTO_DISMISS[severity];
    addNotification(notification, autoDismiss);

    return id;
  }, [addNotification]);

  const showWarning = useCallback((message: string, options?: Omit<ShowErrorOptions, 'severity'>): string => {
    return showError(new Error(message), { ...options, severity: 'warning', message });
  }, [showError]);

  const showInfo = useCallback((message: string, options?: Omit<ShowErrorOptions, 'severity'>): string => {
    return showError(new Error(message), { ...options, severity: 'info', message });
  }, [showError]);

  return (
    <ErrorContext.Provider value={{
      notifications,
      showError,
      showWarning,
      showInfo,
      dismiss,
      dismissAll,
      clearOld,
    }}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useError(): ErrorContextType {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}

/**
 * Error notification display component
 * Renders toast-style notifications in the corner of the screen
 */
export function ErrorNotifications() {
  const { notifications, dismiss } = useError();

  if (notifications.length === 0) return null;

  const getSeverityStyles = (severity: ErrorSeverity) => {
    switch (severity) {
      case 'error':
        return {
          background: 'var(--color-error)',
          borderColor: 'var(--color-error)',
        };
      case 'warning':
        return {
          background: 'var(--color-warning)',
          borderColor: 'var(--color-warning)',
        };
      case 'info':
        return {
          background: 'var(--color-info)',
          borderColor: 'var(--color-info)',
        };
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        maxWidth: '400px',
        width: '100%',
        pointerEvents: 'none',
      }}
    >
      {notifications.map(notification => {
        const severityStyles = getSeverityStyles(notification.severity);
        return (
          <div
            key={notification.id}
            style={{
              background: severityStyles.background,
              color: 'white',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              pointerEvents: 'auto',
              animation: 'slideIn 0.2s ease-out',
            }}
          >
            <span style={{ flex: 1, fontSize: '0.875rem' }}>
              {notification.message}
            </span>
            {notification.action && (
              <button
                onClick={notification.action.onClick}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                {notification.action.label}
              </button>
            )}
            {notification.dismissable && (
              <button
                onClick={() => dismiss(notification.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  opacity: 0.8,
                }}
                aria-label="Dismiss"
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
